import React, { useEffect, useState } from 'react';
import axios from 'axios';
import './VendorBookingDetailPanel.css';

const fmt = (n) => `PKR ${(parseFloat(n) || 0).toLocaleString()}`;
const fmtDate = (d) =>
  d
    ? new Date(d).toLocaleDateString('en-PK', { day: '2-digit', month: 'short', year: 'numeric' })
    : 'N/A';
const getInitials = (s = '') =>
  s.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase() || '??';

const BOOKING_PILL = {
  confirmed: 'vbdp-pill-success',
  pending:   'vbdp-pill-warning',
  cancelled: 'vbdp-pill-danger',
  completed: 'vbdp-pill-info',
};
const PAYMENT_PILL = {
  paid:           'vbdp-pill-success',
  partial_paid:   'vbdp-pill-warning',
  unpaid:         'vbdp-pill-danger',
  refunded:       'vbdp-pill-info',
  pending:        'vbdp-pill-warning',
};

const Pill = ({ status, map }) => (
  <span className={`vbdp-pill ${(map || BOOKING_PILL)[status?.toLowerCase()] || 'vbdp-pill-secondary'}`}>
    {status?.replace(/_/g, ' ') || 'N/A'}
  </span>
);

const Field = ({ label, mono, children }) => (
  <div className="vbdp-field">
    <span className="vbdp-field-label">{label}</span>
    <span className={`vbdp-field-value${mono ? ' mono' : ''}`}>{children || '—'}</span>
  </div>
);

const Divider = () => <div className="vbdp-divider" />;
const SubTitle = ({ children }) => <div className="vbdp-sub-title">{children}</div>;
const AmenityTag = ({ label }) => <span className="vbdp-amenity-tag">{label}</span>;

const parseList = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.map(String).filter(Boolean);
  return String(raw).split(',').map((v) => v.trim()).filter(Boolean);
};

const Chunk = ({ icon, title, accent, children }) => (
  <div className="vbdp-chunk">
    <div className={`vbdp-chunk-header ${accent || ''}`}>
      <span className="vbdp-chunk-header-icon">{icon}</span>
      <span className="vbdp-chunk-title">{title}</span>
    </div>
    <div className="vbdp-chunk-body">{children}</div>
  </div>
);

const StatusBar = ({ bookingStatus, paymentStatus }) => (
  <div className="vbdp-statusbar">
    <div className="vbdp-statusbar-item">
      <span className="vbdp-field-label">Booking Status</span>
      <Pill status={bookingStatus} map={BOOKING_PILL} />
    </div>
    <div className="vbdp-statusbar-item">
      <span className="vbdp-field-label">Payment Status</span>
      <Pill status={paymentStatus} map={PAYMENT_PILL} />
    </div>
  </div>
);

const Timeline = ({ status }) => {
  const isCancelled = status?.toLowerCase() === 'cancelled';
  const steps = isCancelled
    ? ['pending', 'confirmed', 'cancelled']
    : ['pending', 'confirmed', 'completed'];
  const idx = steps.indexOf(status?.toLowerCase());

  return (
    <div className="vbdp-timeline">
      {steps.map((step, i) => {
        const isDone   = i < idx;
        const isActive = i === idx;
        return (
          <div key={step} className="vbdp-tl-item">
            <div className="vbdp-tl-col">
              <div className={`vbdp-tl-dot ${isDone ? 'done' : isActive ? 'active' : ''}`}>
                {isDone ? '✓' : i + 1}
              </div>
              <div className={`vbdp-tl-label ${isDone ? 'done' : isActive ? 'active' : ''}`}>
                {step}
              </div>
            </div>
            {i < steps.length - 1 && (
              <div className={`vbdp-tl-line ${isDone ? 'done' : ''}`} />
            )}
          </div>
        );
      })}
    </div>
  );
};

const VendorBookingDetailPanel = ({ bookingId, onBack, baseUrl, authToken }) => {
  const [b, setB] = useState(null);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');
  const [actionLoading, setActionLoading] = useState('');
  const [alert, setAlert] = useState({ type: '', text: '' });
  const [txnLoading, setTxnLoading] = useState(false);
  const [txnData, setTxnData] = useState(null);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  useEffect(() => {
    setFetchLoading(true);
    setFetchError('');
    axios
      .get(`${baseUrl}/admin/view-booking/${bookingId}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((res) => {
        if (res.data?.success) {
          setB(res.data.data);
        } else {
          setFetchError(res.data?.message || 'Booking detail cannot load.');
        }
      })
      .catch((err) => {
        setFetchError(err.response?.data?.message || 'Network error. Booking not load.');
      })
      .finally(() => setFetchLoading(false));
  }, [bookingId, baseUrl, authToken]);

  const customer       = b?.Jjcustomer       || b?.jjcustomer       || {};
  const vendor         = b?.Jjvendor         || b?.jjvendor         || {};
  const hotel          = vendor?.jjhotelregistration || {};
  const room           = b?.Jjroom           || b?.jjroom           || null;
  const payment        = b?.Jjpayment        || b?.jjpayment        || null;
  const paymentMethods = vendor?.jjpaymentmethods || [];

  const showAlert = (type, text) => {
    setAlert({ type, text });
    setTimeout(() => setAlert({ type: '', text: '' }), 4000);
  };

  const updateStatus = (newStatus) => {
    setActionLoading(newStatus);
    const fd = new FormData();
    fd.append('booking_id', b.id);
    fd.append('booking_status', newStatus);

    axios
      .post(`${baseUrl}/admin/update-booking-status`, fd, {
        headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'multipart/form-data' },
      })
      .then((res) => {
        if (res.data?.success) {
          setB((prev) => ({ ...prev, ...res.data.booking }));
          showAlert('success', `Booking status updated to "${newStatus}" successfully.`);
        } else {
          showAlert('error', res.data?.message || 'Failed to update status.');
        }
      })
      .catch((err) => {
        showAlert('error', err.response?.data?.message || 'Network error. Please try again.');
      })
      .finally(() => setActionLoading(''));
  };

  const viewTransaction = () => {
    if (!payment?.id) return;
    setTxnLoading(true);
    axios
      .get(`${baseUrl}/admin/payments/${payment.id}/stripe-transaction`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((res) => {
        if (res.data?.success) {
          setTxnData(res.data.session);
        } else {
          showAlert('error', res.data?.message || 'Could not fetch transaction.');
        }
      })
      .catch(() => showAlert('error', 'Stripe se transaction fetch nahi ho saka.'))
      .finally(() => setTxnLoading(false));
  };

  if (fetchLoading) {
    return (
      <div className="vbdp-wrapper">
        <div style={{ textAlign: 'center', padding: '4rem' }}>
          <span className="vbdp-spinner" style={{ borderTopColor: '#1E4D6B', width: 28, height: 28 }} />
          <p style={{ marginTop: '1rem', color: 'var(--vbdp-text-mid)' }}>Booking detail load ho raha hai…</p>
        </div>
      </div>
    );
  }

  if (fetchError || !b) {
    return (
      <div className="vbdp-wrapper">
        <div className="vbdp-empty">
          <span className="vbdp-empty-icon">⚠️</span>
          <p>{fetchError || 'Booking nahi mili.'}</p>
          <button className="vbdp-btn vbdp-btn-outline" style={{ marginTop: '1rem' }} onClick={onBack}>
            ← Back to Vendor
          </button>
        </div>
      </div>
    );
  }

  const currentStatus = b.booking_status?.toLowerCase();
  const isCancelled = currentStatus === 'cancelled';
  const isCompleted = currentStatus === 'completed';

  return (
    <div className="vbdp-wrapper">

      <div className="vbdp-page-header">
        <div>
          <h2 className="vbdp-page-title">
            Booking Detail
            <span className="vbdp-booking-id-chip">#{b.id}</span>
          </h2>
          <div className="vbdp-header-meta">
            <Pill status={b.booking_status} map={BOOKING_PILL} />
            <Pill status={b.payment_status} map={PAYMENT_PILL} />
            <span className="vbdp-created-text">Created: {fmtDate(b.created_at)}</span>
          </div>
        </div>
        <button className="vbdp-back-btn" onClick={onBack}>
          ← Back to Vendor
        </button>
      </div>

      {alert.text && (
        <div className={`vbdp-alert vbdp-alert-${alert.type}`}>
          <span>{alert.text}</span>
          <button className="vbdp-alert-close" onClick={() => setAlert({ type: '', text: '' })}>×</button>
        </div>
      )}

      <StatusBar bookingStatus={b.booking_status} paymentStatus={b.payment_status} />

      <Chunk icon="👤" title="1. Customer Information" accent="teal">
        <div className="vbdp-customer-header">
          <div className="vbdp-avatar">
            {getInitials(customer.cus_username || 'NA')}
          </div>
          <div>
            <div className="vbdp-customer-name">{customer.cus_username || 'N/A'}</div>
            <div className="vbdp-customer-badges">
              <span className="vbdp-pill vbdp-pill-secondary">{customer.cus_role || 'Customer'}</span>
              <span className={`vbdp-pill ${customer.cus_status === 'active' ? 'vbdp-pill-success' : 'vbdp-pill-danger'}`}>
                {customer.cus_status?.toUpperCase() || 'N/A'}
              </span>
            </div>
          </div>
        </div>
        <div className="vbdp-field-grid">
          <Field label="Email">{customer.cus_email}</Field>
          <Field label="Phone">{b.phone_no}</Field>
          <Field label="City">{b.cus_city}</Field>
          <Field label="Guests">{b.number_of_guests ? `${b.number_of_guests} Persons` : '—'}</Field>
          <Field label="Customer ID" mono>{customer.id}</Field>
        </div>
      </Chunk>

      <Chunk icon="🏨" title="2. Hotel Information">
        <div className="vbdp-hotel-card">
          <div className="vbdp-hotel-name">{hotel.hotel_name || 'N/A'}</div>
          <div className="vbdp-hotel-meta">
            {hotel.stars && (
              <span>
                <span className="vbdp-stars">{'★'.repeat(hotel.stars)}{'☆'.repeat(5 - hotel.stars)}</span>
                {' '}{hotel.stars}-Star
              </span>
            )}
            {hotel.address    && <span>📍 {hotel.address}</span>}
            {hotel.phone      && <span>📞 {hotel.phone}</span>}
            {hotel.owner_name && <span>👤 Owner: {hotel.owner_name}</span>}
          </div>
          {(hotel.check_in_time || hotel.check_out_time) && (
            <div className="vbdp-hotel-times">
              {hotel.check_in_time  && <span>🟢 Check-in: {hotel.check_in_time}</span>}
              {hotel.check_out_time && <span>🔴 Check-out: {hotel.check_out_time}</span>}
            </div>
          )}
        </div>

        {hotel.facilities && (
          <>
            <SubTitle>Facilities</SubTitle>
            <div className="vbdp-amenities">
              {parseList(hotel.facilities).map((f, i) => <AmenityTag key={i} label={f} />)}
            </div>
          </>
        )}

        {hotel.description && (
          <>
            <Divider />
            <SubTitle>Description</SubTitle>
            <p style={{ fontSize: '0.82rem', color: 'var(--vbdp-text-mid)', margin: 0, lineHeight: 1.6 }}>
              {hotel.description}
            </p>
          </>
        )}
      </Chunk>

      <Chunk icon="🛏️" title="3. Booked Room Details" accent="gold">
        {room ? (
          <div className="vbdp-room-card">
            <div className="vbdp-room-header">
              <span className="vbdp-room-type-pill">{room.room_type || 'Room'}</span>
              <span className={`vbdp-pill ${room.status?.toLowerCase() === 'available' ? 'vbdp-pill-success' : 'vbdp-pill-warning'}`}>
                {room.status?.toUpperCase() || 'N/A'}
              </span>
            </div>
            <div className="vbdp-field-grid">
              <Field label="Room Number" mono>{room.room_number}</Field>
              <Field label="Floor">{room.floor}</Field>
              <Field label="Capacity">{room.capacity ? `${room.capacity} Persons` : '—'}</Field>
              <Field label="Price / Night">{fmt(room.price)}</Field>
            </div>

            {room.amenities && (
              <>
                <Divider />
                <SubTitle>Amenities</SubTitle>
                <div className="vbdp-amenities">
                  {parseList(room.amenities).map((a, i) => <AmenityTag key={i} label={a} />)}
                </div>
              </>
            )}

            {room.jjroomimages?.length > 0 && (
              <>
                <Divider />
                <SubTitle>Room Images</SubTitle>
                <div className="vbdp-room-images">
                  {room.jjroomimages.map((img) => (
                    <img key={img.id} src={img.image_path} alt="Room" className="vbdp-room-img" />
                  ))}
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="vbdp-empty">
            <span className="vbdp-empty-icon">🚪</span>
            <p>No room information available for this booking.</p>
          </div>
        )}
      </Chunk>

      <Chunk icon="📅" title="4. Booking Information" accent="info">
        <div className="vbdp-id-status-row">
          <Field label="Booking ID" mono>{b.id}</Field>
          <Pill status={b.booking_status} map={BOOKING_PILL} />
        </div>
        <div className="vbdp-field-grid">
          <Field label="Booking Date">{fmtDate(b.created_at)}</Field>
          <Field label="Check-In">{fmtDate(b.checkIn || b.check_in)}</Field>
          <Field label="Check-Out">{fmtDate(b.checkOut || b.check_out)}</Field>
          <Field label="Guests">{b.number_of_guests ? `${b.number_of_guests} Persons` : '—'}</Field>
          <Field label="Booking Type">
            <span className="vbdp-pill vbdp-pill-info">{b.booking_type?.toUpperCase() || 'HOTEL'}</span>
          </Field>
          <Field label="Total Amount">
            <strong style={{ color: '#27ae60', fontSize: '0.95rem' }}>{fmt(b.total_amount)}</strong>
          </Field>
          <Field label="Reservation Amount">{fmt(b.reservation_amount)}</Field>
          <Field label="Remaining Amount">{fmt(b.remaining_amount)}</Field>
        </div>
        <Divider />
        <SubTitle>Status Timeline</SubTitle>
        <Timeline status={b.booking_status} />
      </Chunk>

      <Chunk icon="🏢" title="5. Vendor Information" accent="grey">
        <SubTitle>Vendor Profile</SubTitle>
        <div className="vbdp-field-grid" style={{ marginBottom: '1rem' }}>
          <Field label="Vendor Name">{vendor.name}</Field>
          <Field label="Email">{vendor.email}</Field>
          <Field label="Role">{vendor.role?.toUpperCase() || 'VENDOR'}</Field>
          <Field label="Status">
            <span className={`vbdp-pill ${vendor.verification_status === 'active' ? 'vbdp-pill-success' : 'vbdp-pill-warning'}`}>
              {vendor.verification_status?.toUpperCase() || 'N/A'}
            </span>
          </Field>
          <Field label="Vendor ID" mono>{vendor.id}</Field>
        </div>

        <Divider />
        <SubTitle>Registered Payment Methods</SubTitle>
        {paymentMethods.length > 0 ? (
          paymentMethods.map((pm, i) => (
            <div key={pm.id || i} className="vbdp-vendor-acct-card">
              <div className="vbdp-field-grid">
                <Field label="Name">{pm.name}</Field>
                <Field label="Type">{pm.type?.replace(/_/g, ' ').toUpperCase()}</Field>
                <Field label="Account No." mono>{pm.account_number}</Field>
              </div>
            </div>
          ))
        ) : (
          <p style={{ fontSize: '0.82rem', color: 'var(--vbdp-text-light)' }}>No bank account registered.</p>
        )}
      </Chunk>

      <Chunk icon="💳" title="6. Payment Information" accent="green">
        {payment ? (
          <>
            <div className="vbdp-id-status-row">
              <Field label="Payment ID" mono>{payment.id}</Field>
              <Pill status={b.payment_status} map={PAYMENT_PILL} />
            </div>
            <div className="vbdp-field-grid" style={{ marginBottom: '1rem' }}>
              <Field label="Stripe Session ID" mono>{payment.stripe_checkout_session_id || '—'}</Field>
              <Field label="Paid At">{fmtDate(payment.paid_at)}</Field>
              <Field label="Settlement Status">
                <span className={`vbdp-pill ${payment.status === 'paid' ? 'vbdp-pill-success' : 'vbdp-pill-warning'}`}>
                  {payment.status?.toUpperCase() || 'PENDING'}
                </span>
              </Field>
            </div>

            {payment.stripe_checkout_session_id && (
              <>
                <button className="vbdp-btn vbdp-btn-outline" onClick={viewTransaction} disabled={txnLoading}>
                  {txnLoading ? <><span className="vbdp-spinner" /> Fetching…</> : '🔎 View Transaction'}
                </button>

                {txnData && (
                  <div className="vbdp-breakdown" style={{ marginTop: '0.9rem' }}>
                    <div className="vbdp-breakdown-row">
                      <span>Stripe Payment Status</span>
                      <strong>{txnData.payment_status}</strong>
                    </div>
                    <div className="vbdp-breakdown-row">
                      <span>Amount Charged</span>
                      <strong>{fmt((txnData.amount_total || 0) / 100)}</strong>
                    </div>
                    <div className="vbdp-breakdown-row">
                      <span>Payment Intent ID</span>
                      <strong className="mono">{txnData.payment_intent?.id || '—'}</strong>
                    </div>
                    <div className="vbdp-breakdown-row">
                      <span>Payment Method</span>
                      <strong>{txnData.payment_intent?.payment_method_types?.[0]?.toUpperCase() || '—'}</strong>
                    </div>
                  </div>
                )}
                <Divider />
              </>
            )}

            <SubTitle>Payment Breakdown</SubTitle>
            <div className="vbdp-breakdown">
              <div className="vbdp-breakdown-row">
                <span>Total Booking Amount</span>
                <strong style={{ color: '#27ae60' }}>{fmt(payment.total_amount || b.total_amount)}</strong>
              </div>
              <div className="vbdp-breakdown-row">
                <span>Platform Commission (Reserved for Admin)</span>
                <strong style={{ color: '#e74c3c' }}>— {fmt(payment.admin_commission)}</strong>
              </div>
              <div className="vbdp-breakdown-row vbdp-breakdown-total">
                <span>Vendor Payout</span>
                <strong style={{ color: '#1e4d6b' }}>{fmt(payment.vendor_amount)}</strong>
              </div>
            </div>
          </>
        ) : (
          <div className="vbdp-empty">
            <span className="vbdp-empty-icon">💳</span>
            <p>No payment record found for this booking.</p>
          </div>
        )}
      </Chunk>

      <div className="vbdp-actions">
        {currentStatus === 'pending' && (
          <button
            className="vbdp-btn vbdp-btn-success"
            onClick={() => updateStatus('confirmed')}
            disabled={!!actionLoading}
          >
            {actionLoading === 'confirmed'
              ? <><span className="vbdp-spinner" /> Confirming…</>
              : '✓ Confirm Booking'}
          </button>
        )}

      


        <button className="vbdp-btn vbdp-btn-outline" onClick={onBack}>
          ← Back to Vendor
        </button>
      </div>
    </div>
  );
};

export default VendorBookingDetailPanel;